// Encuestas creadas: lista con filtros (estado, carpeta), búsqueda, duplicar y eliminar.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Modal, Select, TableSkeleton } from "../../../components/ui.tsx";
import { SearchInput } from "../../../components/SearchInput.tsx";
import { toast } from "../../../components/toast.tsx";
import { FolderNav } from "../forms/shared.tsx";
import type { Survey, SurveyListRow } from "./types.ts";

export function SurveyList(): JSX.Element {
  const admin = Boolean(useMe().data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [folder, setFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (status) params.set("status", status);
  if (folder) params.set("folder", folder);
  const list = useQuery<{ rows: SurveyListRow[]; folders: string[] }>({
    queryKey: ["mkt-surveys", q, status, folder],
    queryFn: () => api.get(`/mkt/surveys${params.toString() ? `?${params}` : ""}`),
  });
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-surveys"] }); };
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");

  const create = useMutation({
    mutationFn: () => api.post<{ survey: Survey }>("/mkt/surveys", { name: name.trim(), ...(folder ? { folder } : {}) }),
    onSuccess: (r) => { inval(); nav(`${r.survey.id}/editar`); }, onError: onErr,
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.post(`/mkt/surveys/${id}/duplicate`),
    onSuccess: () => { toast.success("Encuesta duplicada (borrador)."); inval(); }, onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/surveys/${id}`),
    onSuccess: () => { toast.success("Encuesta eliminada."); inval(); }, onError: onErr,
  });

  const rows = list.data?.rows ?? [];
  const go = (id: string, tab?: string): void => void nav(tab ? `${id}?tab=${tab}` : id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="text-lg font-semibold text-ink">Encuestas creadas</h2>
        <div className="flex flex-wrap gap-2">
          <SearchInput value={q} onValueChange={setQ} placeholder="Buscar encuesta…" className="w-56" />
          <Select className="w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
            <option value="">Todos los estados</option><option value="published">Publicada</option><option value="draft">Borrador</option>
          </Select>
          {admin && <Button variant="primary" onClick={() => setCreating(true)}><Plus size={16} strokeWidth={1.75} /> Nueva encuesta</Button>}
        </div>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva encuesta"
        footer={<><Button onClick={() => setCreating(false)}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Crear y editar</Button></>}>
        <Field label="Nombre" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoFocus placeholder="Ej.: Satisfacción clientes 2026" /></Field>
        <p className="text-xs text-ink-muted mt-3">Se crea como borrador con preguntas de ejemplo (nombre, correo, NPS y comentario) que puedes cambiar.</p>
      </Modal>

      <div className="flex flex-col md:flex-row gap-4">
        <FolderNav folders={list.data?.folders ?? []} value={folder} onChange={setFolder} />
        <Card className="flex-1 min-w-0 overflow-hidden">
          {list.isLoading ? <TableSkeleton rows={5} cols={7} />
            : list.isError ? <ErrorState message="No se pudieron cargar las encuestas." onRetry={() => void list.refetch()} />
            : !rows.length ? <EmptyState title="Sin encuestas" body={q || status || folder ? "Ninguna encuesta coincide con los filtros." : "Crea una encuesta para conocer la opinión de tus contactos."} />
            : (
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full">
                  <thead><tr>
                    <th className="th">Encuesta</th><th className="th">Creada por</th><th className="th">Estado</th><th className="th">Creación</th>
                    <th className="th text-right">Impresiones</th><th className="th text-right">Respuestas</th><th className="th text-right">Contactos creados</th><th className="th" />
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-surface-hover cursor-pointer" onClick={() => go(r.id)}>
                        <td className="td"><div className="font-medium text-ink min-w-[180px]">{r.name}</div>{r.folder && <div className="text-xs text-ink-muted">{r.folder}</div>}</td>
                        <td className="td whitespace-nowrap">{r.creator ?? "—"}</td>
                        <td className="td"><Badge color={r.status === "published" ? "green" : "gray"}>{r.status === "published" ? "Publicada" : "Borrador"}</Badge></td>
                        <td className="td whitespace-nowrap">{formatDate(r.createdAt)}</td>
                        <td className="td text-right tnum">{r.impressions}</td>
                        <td className="td text-right"><button type="button" className={`tnum ${r.responsesCount ? "link" : "text-ink-muted"}`} onClick={(e) => { e.stopPropagation(); go(r.id, "respuestas"); }}>{r.responsesCount}</button></td>
                        <td className="td text-right tnum">{r.contactsCreated}</td>
                        <td className="td" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {admin && <button type="button" className="btn-ghost btn-sm" title="Duplicar" onClick={() => dup.mutate(r.id)}><Copy size={14} /></button>}
                            {admin && <button type="button" className="btn-ghost btn-sm text-danger" title="Eliminar"
                              onClick={() => { if (window.confirm(`¿Eliminar la encuesta «${r.name}» y todas sus respuestas?`)) del.mutate(r.id); }}><Trash2 size={14} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
